import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  List,
  CircularProgress,
  Typography,
  Button,
  Paper,
  Backdrop,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import HelpHint from "../parts/helpHint";
import { toast } from "react-toastify";
import { DistributionsApi } from "../../services/api/distributions";
import ControlBar from "../dashboard/controlBar";
import DistributionItemWrapper from "./distributionItem";

function Distribution(props) {
  const [isLoading, setIsLoading] = useState(true);
  const [distributions, setDistributions] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isMounted = useRef(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getDistributionEndpoint = async ({ showOverlay = false } = {}) => {
    if (showOverlay && isMounted.current) {
      setIsRefreshing(true);
    }
    try {
      let token = props.token;
      if (typeof token === 'function') {
        token = await token();
      }
      if (!token || typeof token !== 'string' || token === 'undefined') {
        toast.error('No valid token found for distribution API call.');
        return;
      }

      const response = await DistributionsApi.getDistributions(token);
      
      if (response && response.success) {
        if (isMounted.current) {
          setDistributions(response.data);
          // Store with user ID to ensure user-specific caching
          const cacheKey = `distributions_${props.user?.sub || 'unknown'}`;
          localStorage.setItem(cacheKey, JSON.stringify(response.data));
        }
      } else {
        toast.error("Failed to fetch distributions");
      }
    } catch (error) {
      toast.error("Error fetching distributions");
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
        if (showOverlay) {
          setIsRefreshing(false);
        }
      }
    }
  };

  // Handler for when individual distributions are updated
  const handleDistributionUpdate = async (showOverlay = true) => {
    await getDistributionEndpoint({ showOverlay });
  };

  useEffect(() => {
    isMounted.current = true;
    window.scrollTo(0, 0);

    // Use user-specific cache key
    const cacheKey = `distributions_${props.user?.sub || 'unknown'}`;
    const cachedDistributions = localStorage.getItem(cacheKey);
    if (cachedDistributions) {
      setDistributions(JSON.parse(cachedDistributions));
      setIsLoading(false);
    }

    getDistributionEndpoint();
    // Adaptive polling: fast (10s) when any items processing, slow (60s) otherwise
    let intervalMs = 60000;
    const decideInterval = () => {
      const hasProcessing = distributions.some(d => d.distributionStatus === 'PROCESSING');
      return hasProcessing ? 10000 : 60000;
    };
    intervalMs = decideInterval();
    let intervalID = setInterval(() => {
      intervalMs = decideInterval();
      getDistributionEndpoint();
      // If interval changed, reset timer
      clearInterval(intervalID);
      intervalID = setInterval(() => {
        intervalMs = decideInterval();
        getDistributionEndpoint();
      }, intervalMs);
    }, intervalMs);

    return () => {
      isMounted.current = false;
      clearInterval(intervalID);
    };
  }, [props.user?.sub]); // Add user dependency to reload on user change

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ 
          width: isMobile ? "95vw" : "100%",
          px: { xs: 1, sm: 2, md: 3 }, 
          pb: { xs: 2, sm: 2, md: 2 },
          mx: "auto",
          boxSizing: "border-box"
        }}>
        <ControlBar
          Button1="Add Distribution"
          ButtonLink="/distributions/add/"
          helpHint={
            <HelpHint
              title="Distributions"
              hints={[
                "Create a new distribution with the Add Distribution button",
                "Distributions send your collections to a tier",
                "Filter by status to find scheduled or completed sends",
              ]}
            />
          }
        />
      </Box>

      {!distributions.length ? (
        <Box sx={{ 
          width: isMobile ? "95vw" : "100%",
          px: { xs: 0.5, sm: 2, md: 3 }, 
          pb: { xs: 10, sm: 6, md: 4 },
          mx: "auto",
          boxSizing: "border-box"
        }}>
          <Box sx={{ 
            textAlign: 'center', 
            py: { xs: 4, sm: 8 }, 
            backgroundColor: 'background.paper',
            borderRadius: { xs: 1, sm: 2 },
            border: '2px dashed',
            borderColor: 'divider',
            mt: 3,
            mx: { xs: 0.5, sm: 0 }
          }}>
            <Typography variant="h4" color="text.secondary" sx={{ mb: 2, fontWeight: 300 }}>
              No Distributions Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              Create your first distribution to send protected photos to your clients securely.
            </Typography>
            <Button
              component={Link}
              to="/distributions/add/"
              variant="contained"
              size="large"
              sx={{ px: 4, py: 1.5 }}
            >
              Create Your First Distribution
            </Button>
          </Box>
        </Box>
        ) : (
          <Box sx={{ 
            width: isMobile ? "95vw" : "100%",
            px: { xs: 0.5, sm: 2, md: 3 }, 
            pb: { xs: 10, sm: 6, md: 4 }, 
            mt: 1,
            mx: "auto",
            boxSizing: "border-box"
          }}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper 
                sx={{ 
                  mt: 0, // Remove extra margin between button and list
                  mx: { xs: 0.5, sm: 0 },
                  borderRadius: { xs: 1, sm: 2 },
                  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 8px',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ p: { xs: 1.5, sm: 3 }, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "primary.main", fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    Your Distributions ({distributions.filter(item => item.distributionStatus !== 'CANCELLED').length})
                  </Typography>
                </Box>
                <List sx={{ bgcolor: 'background.paper', p: 0 }}>
                  {distributions
                    .filter(item => item.distributionStatus !== 'CANCELLED')
                    .map((item, index, filteredArray) => (
                    <DistributionItemWrapper 
                      key={item.id || index} 
                      item={item}
                      token={props.token}
                      onDistributionUpdate={handleDistributionUpdate}
                      sx={{
                        borderBottom: index < filteredArray.length - 1 ? '1px solid' : 'none',
                        borderBottomColor: 'divider'
                      }}
                    />
                  ))}
                </List>
              </Paper>
            </motion.div>
          </Box>
        )}
      <Backdrop
        sx={{ color: '#fff', zIndex: (t) => t.zIndex.modal + 1 }}
        open={isRefreshing}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}

export default Distribution;
